import { useEffect, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../utils/apiClient';
import './LiveStatsBar.css';

interface LiveData {
  todayRevenue: number;
  todayOrders: number;
  activeOrders: number;
  averageOrderValue: number;
  upsellRevenue: number;
  upsellConversionRate: number;
  topUpsellName: string | null;
  hourly: Array<{ hour: number; revenue: number; upsellRevenue: number; orders: number }>;
}

interface Props {
  restaurantId: string;
  onRegisterRefresh: (cb: () => void) => void;
}

const fmt = (n: number) => `₸${Math.round(n).toLocaleString('ru-RU')}`;

export function LiveStatsBar({ restaurantId, onRegisterRefresh }: Props) {
  const { t } = useTranslation();
  const [data, setData] = useState<LiveData | null>(null);
  const [loading, setLoading] = useState(true);
  const prevRef = useRef<LiveData | null>(null);
  const [flashKeys, setFlashKeys] = useState<Set<string>>(new Set());
  const [tooltip, setTooltip] = useState<{ x: number; y: number; content: string } | null>(null);

  const fetchLive = useCallback(async () => {
    try {
      const { data: live } = await api.get<LiveData>(`/api/analytics/restaurant/${restaurantId}/live`);
      setData(prev => {
        if (prev) {
          const changed = new Set<string>();
          if (prev.todayRevenue !== live.todayRevenue)               changed.add('revenue');
          if (prev.todayOrders !== live.todayOrders)                 changed.add('orders');
          if (prev.activeOrders !== live.activeOrders)               changed.add('active');
          if (prev.averageOrderValue !== live.averageOrderValue)     changed.add('aov');
          if (prev.upsellRevenue !== live.upsellRevenue)             changed.add('upsellRev');
          if (prev.upsellConversionRate !== live.upsellConversionRate) changed.add('upsellCon');
          if (changed.size > 0) {
            setFlashKeys(changed);
            setTimeout(() => setFlashKeys(new Set()), 850);
          }
        }
        prevRef.current = prev;
        return live;
      });
    } catch {
      // silently ignore — show stale data
    } finally {
      setLoading(false);
    }
  }, [restaurantId]);

  useEffect(() => {
    onRegisterRefresh(fetchLive);
  }, [fetchLive, onRegisterRefresh]);

  useEffect(() => {
    setLoading(true);
    setData(null);
    fetchLive();
  }, [restaurantId, fetchLive]);

  const renderChart = (hourly: LiveData['hourly']) => {
    const W = 600;
    const H = 80;
    const barW = W / hourly.length;
    const maxRev = Math.max(...hourly.map(h => h.revenue), 1);

    return (
      <div className="live-chart-container">
        <div className="live-chart-title">{t('analytics.live.revenueByHour')}</div>
        <svg
          viewBox={`0 0 ${W} ${H + 20}`}
          className="live-chart-svg"
          style={{ display: 'block' }}
        >
          {hourly.map((h, i) => {
            const totalH = (h.revenue / maxRev) * H;
            const upsellH = h.revenue > 0 ? (h.upsellRevenue / h.revenue) * totalH : 0;
            const baseH = totalH - upsellH;
            const x = i * barW + 2;
            const bw = barW - 4;

            return (
              <g key={`${h.hour}-${i}`}
                onMouseMove={(e) => setTooltip({
                  x: e.clientX + 12,
                  y: e.clientY - 10,
                  content: `${h.hour}:00\n${t('analytics.live.tooltipRevenue')}: ${fmt(h.revenue)}\n${t('analytics.live.tooltipUpsells')}: ${fmt(h.upsellRevenue)}\n${t('analytics.live.tooltipOrders')}: ${h.orders}`,
                })}
                onMouseLeave={() => setTooltip(null)}
                style={{ cursor: 'default' }}
              >
                {baseH > 0 && (
                  <rect x={x} y={H - totalH} width={bw} height={baseH} fill="#f5a623" rx={2} opacity={0.8} />
                )}
                {upsellH > 0 && (
                  <rect x={x} y={H - upsellH} width={bw} height={upsellH} fill="#22c55e" rx={2} opacity={0.9} />
                )}
                {totalH === 0 && (
                  <rect x={x} y={H - 2} width={bw} height={2} fill="rgba(255,255,255,0.08)" rx={1} />
                )}
                <text x={x + bw / 2} y={H + 14} textAnchor="middle" fontSize={9} fill="rgba(255,255,255,0.35)">
                  {`${h.hour}:00`}
                </text>
              </g>
            );
          })}
        </svg>
        {tooltip && (
          <div
            className="live-chart-tooltip"
            style={{ left: tooltip.x, top: tooltip.y }}
          >
            {tooltip.content.split('\n').map((line, i) => <div key={i}>{line}</div>)}
          </div>
        )}
      </div>
    );
  };

  const cards = [
    { key: 'revenue',   label: t('analytics.live.revenueToday'),     value: data ? fmt(data.todayRevenue) : '—' },
    { key: 'orders',    label: t('analytics.live.ordersToday'),      value: data ? String(data.todayOrders) : '—' },
    { key: 'active',    label: t('analytics.live.activeNow'),        value: data ? String(data.activeOrders) : '—', pulse: (data?.activeOrders ?? 0) > 0 },
    { key: 'aov',       label: t('analytics.live.avgCheck'),         value: data ? fmt(data.averageOrderValue) : '—' },
    { key: 'upsellRev', label: t('analytics.live.upsellStat'),       value: data ? fmt(data.upsellRevenue) : '—' },
    { key: 'upsellCon', label: t('analytics.live.upsellConversion'), value: data ? `${data.upsellConversionRate.toFixed(1)}%` : '—' },
  ];

  return (
    <div className="live-stats-bar">
      <div className="live-stats-grid">
        {cards.map(card => (
          <div
            key={card.key}
            className={`live-stat-card${loading ? ' skeleton' : ''}${flashKeys.has(card.key) ? ' flash' : ''}`}
          >
            <div className="live-stat-label">{card.label}</div>
            <div className="live-stat-value">
              {(card as any).pulse && <span className="pulse-dot" />}
              {card.value}
            </div>
            {card.key === 'upsellCon' && data?.topUpsellName && (
              <div className="live-stat-sub">{t('analytics.live.top')}: {data.topUpsellName}</div>
            )}
          </div>
        ))}
      </div>

      {data?.hourly && renderChart(data.hourly)}
    </div>
  );
}
